import { h, Component } from 'preact';
import cx from 'classnames';
import { Localizer } from 'preact-i18n';
import Nav from '.';
import LinkItem from './link-item';
import style from './style';
import { currentSkinTier, nextSkinTiers } from './util';

/**
 * MenuItems are rendered when a nav item has a 'submenu' array.
 * They are made up of both a LinkItem as well as a child Nav.
 */
export default class MenuItem extends Component {
	state = {

		/** Controls menu visibility */
		open: this.props.open || false,
		openLeft: false
	};

	handleLinkItemClick = e => {
		if (this.focused || !this.props.type || this.props.type === 'click') {
			this.toggle();
		}
		else {
			this.close();
			if (this.props.onLinkItemClick) this.props.onLinkItemClick(e);
		}
	}

	/** Close child menu if it is open */
	close = () => {
		if (this.state.open) {
			this.setState({ open: false });
			this.notifyMenuOpen();
		}
	};

	/** Open child menu if not already open */
	open = () => {
		if (!this.state.open) {
			this.setState({ open: true });
			this.notifyMenuOpen();
		}
	};

	notifyMenuOpen = () => {
		if (this.props.onMenuOpen){
			let open = !!(this.state.open || this.props.open);
			if (open !== this.menuOpen) {
				this.menuOpen = open;
				this.props.onMenuOpen(open, this.props.item, { type: this.props.type, index: this.props.positionIndex });
			}

		}
	}

	notifySubMenuOpen = (isOpen, link, meta) => {
		if (this.props.onMenuOpen){
			this.props.onMenuOpen(isOpen, link, meta, true);
		}
	}

	/** Toggles the nested navigation menu open or closed based on current state */
	toggle = () => {
		this.state.open ? this.close(): this.open();
	}

	/** If the given click event is outside this menu, close */
	onClickOutside = ({ target }) => {
		do {
			if (target===this.base) return;
		} while ((target=target.parentNode));
		this.close();
	};

	compRef = c => {
		this.comp = c;
	};

	/** Stores window width checks so respositioning is only done if something changes. */
	updateRepositionCache = cache => {
		if (this.cacheReposition
			&& (
				this.cacheReposition.width === cache.width
				&& this.cacheReposition.windowWidth === cache.windowWidth
			)
		) {
			return false;
		}
		this.cacheReposition = cache;
		return true;
	}

	/** Clear the cache we use to optimize positioning. */
	clearRepositionCache = () => this.cacheReposition = null;

	/** Conditionally reposition child nav to remain on-screen */
	repositionNav = () => {
		let comp = this.comp && this.comp.base;
		if (!comp) return;

		let width = comp.offsetWidth,
			{ left } = comp.getBoundingClientRect && comp.getBoundingClientRect(),
			windowWidth = window.innerWidth;

		if (!this.updateRepositionCache({ width, windowWidth, left })) return;

		let openLeft = this.state.openLeft,
			right = left + width * (openLeft ? 2 : 1),
			shouldOpenLeft = right > windowWidth;

		if (shouldOpenLeft !== openLeft) {
			this.setState({ openLeft: shouldOpenLeft });
		}

	};

	handleRef = (element) => {
		this.addMouseListeners(element);
		this.rootElement = element;
	}

	handleFocus = () => {
		this.focused = true;
		this.setState({ focused: true });
	}

	handleBlur = e => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			this.close();
			this.setState({ focused: false });
		}
	}

	addMouseListeners(element) {
		this.clearMouseListeners();
		if (element && element.addEventListener){
			if (this.props.type === 'hover') {
				element.addEventListener('mouseenter', this.open);
				element.addEventListener('mouseleave', this.close);
			}
			element.addEventListener('focusin', this.handleFocus);
			element.addEventListener('focusout', this.handleBlur);
		}
		addEventListener('click', this.onClickOutside);
	}

	clearMouseListeners() {
		if (this.rootElement) {
			if (this.props.type === 'hover') {
				this.rootElement.removeEventListener('mouseenter', this.open);
				this.rootElement.removeEventListener('mouseleave', this.close);
			}
			this.rootElement.removeEventListener('focusin', this.handleFocus);
			this.rootElement.removeEventListener('focusout', this.handleBlur);
		}
		removeEventListener('click', this.onClickOutside);
	}

	allowSubnavRender = () => {
		this.setState({ subnavCanRender: true });
	}

	componentDidMount() {
		//We use a short timeout to ensure the check is made later so the nav can render.
		if (this.state.open) setTimeout(() => this.repositionNav());
	}

	componentWillReceiveProps({ open }) {
		// If `open` prop changes, open/close accordingly
		if (open !== this.props.open) {
			this.clearRepositionCache();
			this.setState({ open });
		}
	}

	componentDidUpdate(_, { open }) {

		/*
			After rendering, if the nav was just opened, ensure it is on-screen.
			We use a short timeout to ensure the check is made later so the nav can render.
		*/
		if (this.state.open && !open) setTimeout(() => this.repositionNav());
	}

	componentWillUnmount() {
		this.clearMouseListeners();
	}

	render({ item, current, type, innerType, skin, linkRenderer, menuPropsGenerator, open: openProp, openLeft: openLeftProp, positionIndex, delaySubnavRender, ...props }, { open, openLeft, focused, subnavCanRender }) {

		open = open || openProp;
		openLeft = openLeft || openLeftProp;

		/*
		This is used to populate extra props on sub menus.
		Used primarily for adding props related to accessibility:
		*/
		menuPropsGenerator = menuPropsGenerator || currentSkinTier(skin).menuPropsGenerator;
		let doSubnavRender = this.hasSubnavRendered || !delaySubnavRender || subnavCanRender || open;
		if (doSubnavRender) this.hasSubnavRendered = true;

		return (
			<div
				{...props}
				ref={this.handleRef}
				class={cx(
					style.menu,
					currentSkinTier(skin).menu,
					open ? style.opened : style.closed,
					openLeft && style.openLeft
				)}
			>
				<LinkItem
					item={item}
					id={item.labelId}
					suppressLink={type === 'click'}
					class={cx(props.class)}
					current={current}
					aria-haspopup
					aria-expanded={open}
					skin={skin}
					linkRenderer={linkRenderer}
					isMenuItem
					menuIsOpen={open}
					onLinkItemClick={this.handleLinkItemClick}
					positionIndex={positionIndex}
					onSubnavCanRender={this.allowSubnavRender}
				/>
				{

					/*
					By setting delaySubnavRender=true, the subnavs will not be rendered
					until the parent item is opened, or if onSubnavCanRender() is called by
					a the LinkItem child.
					*/
					doSubnavRender && (
						<Localizer>
							<Nav
								role="menu"
								items={item.subnav}
								current={current}
								type={innerType || type}
								delaySubnavRender={delaySubnavRender}
								ref={this.compRef}
								class={cx(style.subnav, !open ? style.closed : style.opened)}
								openLeft={openLeft /*eslint-disable-line no-unneeded-ternary*/}
								skin={nextSkinTiers(skin)}
								aria-hidden={!open}
								linkRenderer={linkRenderer}
								onLinkItemClick={this.handleLinkItemClick}
								onMenuOpen={this.notifySubMenuOpen}
								{...(menuPropsGenerator ? menuPropsGenerator(item) : {})}
							/>
						</Localizer>
					)
				}

			</div>
		);
	}
}
